/**
 * Which cabinet to open for an account that holds both application roles.
 *
 * Mirrors `mobile/src/auth/roles.ts` — the two clients must agree, or the same account lands in
 * different cabinets depending on where it signed in.
 */

/**
 * Priority order when the user has expressed no preference yet. **Driver comes first, and that is
 * deliberate.**
 *
 * `manager` is the role people end up with by default: it is free, self-serve, and both identity
 * providers hand it out through a hardcoded-role mapper on every social sign-in. `driver` is only
 * ever acquired on purpose — chosen at registration, or granted by an explicit action. So of the
 * two, holding `driver` is the stronger statement about who this person is.
 *
 * Ordered the other way round, a driver who signed in with Яндекс or VK picked up `manager` from
 * the mapper and silently landed in the manager cabinet.
 */
export const ROLE_PRIORITY = ['driver', 'manager'] as const

/** The cabinet to default to, or '' when the token carries neither application role. */
export function pickPrimaryRole(roles: string[]): string {
  return ROLE_PRIORITY.find((role) => roles.includes(role)) ?? ''
}

/**
 * The cabinet to show: the user's stored choice while they still hold that role, otherwise the
 * priority above. An explicit switch always outranks the default — that is what keeps this safe
 * for anyone who deliberately works in the other cabinet.
 */
export function resolveActiveRole(roles: string[], stored: string | null): string {
  return stored && roles.includes(stored) ? stored : pickPrimaryRole(roles)
}
